import { useState } from 'react'
import { Head, Form } from '@inertiajs/react'
import type { Keg, Establishment } from '@/types'

type OperationType = 'filling' | 'delivery' | 'pickup' | 'incident'

type Labels = {
  page_title: string
  current_status_prefix: string
  operation_label: string
  operation_type_labels: Record<OperationType, string>
  establishment_label: string
  no_establishment_option: string
  location_label: string
  notes_label: string
  beer_label: string
  submit: string
  submit_processing: string
}

type Props = {
  keg: Keg
  establishments: Establishment[]
  labels: Labels
}

const inputClass =
  'mt-1 block w-full rounded-md border-cream-300 bg-cream-50 text-bark-900 focus:border-amber-500 focus:ring-amber-500'

export default function PublicKegsShow({ keg, establishments, labels }: Props) {
  const [operationType, setOperationType] = useState<OperationType>('delivery')

  return (
    <>
      <Head title={labels.page_title}>
        <meta name="robots" content="noindex" />
      </Head>
      <div className="mx-auto max-w-md px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="font-display text-2xl text-bark-900">{keg.identifier}</h1>
        <p className="mt-1 text-sm text-bark-600">
          {labels.current_status_prefix}
          <span className="font-medium text-bark-900">{keg.status_label}</span>
        </p>

        <Form method="post" action="/kegs/operations" className="mt-6 space-y-6">
          {({ errors, processing }) => (
            <>
              <input type="hidden" name="tokens[]" value={keg.token} />

              <div>
                <label htmlFor="operation_type" className="block text-sm font-medium text-bark-700">
                  {labels.operation_label}
                </label>
                <select
                  id="operation_type"
                  name="operation_type"
                  value={operationType}
                  onChange={(e) => setOperationType(e.target.value as OperationType)}
                  className={inputClass}
                >
                  <option value="filling">{labels.operation_type_labels.filling}</option>
                  <option value="delivery">{labels.operation_type_labels.delivery}</option>
                  <option value="pickup">{labels.operation_type_labels.pickup}</option>
                  <option value="incident">{labels.operation_type_labels.incident}</option>
                </select>
                {errors.operation_type && (
                  <p className="mt-1 text-sm text-rust-600">{errors.operation_type}</p>
                )}
              </div>

              {(operationType === 'delivery' || operationType === 'pickup') && (
                <>
                  <div>
                    <label
                      htmlFor="establishment_id"
                      className="block text-sm font-medium text-bark-700"
                    >
                      {labels.establishment_label}
                    </label>
                    <select id="establishment_id" name="establishment_id" className={inputClass}>
                      <option value="">{labels.no_establishment_option}</option>
                      {establishments.map((establishment) => (
                        <option key={establishment.id} value={establishment.id}>
                          {establishment.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-medium text-bark-700">
                      {labels.location_label}
                    </label>
                    <input id="location" name="location" type="text" className={inputClass} />
                  </div>
                </>
              )}

              {operationType === 'filling' && (
                <div>
                  <label htmlFor="beer" className="block text-sm font-medium text-bark-700">
                    {labels.beer_label}
                  </label>
                  <input
                    id="beer"
                    name="beer"
                    type="text"
                    defaultValue={keg.current_beer ?? ''}
                    className={inputClass}
                  />
                  {errors.beer && <p className="mt-1 text-sm text-rust-600">{errors.beer}</p>}
                </div>
              )}

              {operationType === 'incident' && (
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-bark-700">
                    {labels.notes_label}
                  </label>
                  <textarea id="notes" name="notes" rows={3} className={inputClass} />
                  {errors.notes && <p className="mt-1 text-sm text-rust-600">{errors.notes}</p>}
                </div>
              )}

              {errors.base && <p className="text-sm text-rust-600">{errors.base}</p>}

              <button
                type="submit"
                disabled={processing}
                className="w-full rounded-md bg-hop-600 px-4 py-2.5 text-sm font-medium text-cream-50 hover:bg-hop-700 disabled:opacity-50"
              >
                {processing ? labels.submit_processing : labels.submit}
              </button>
            </>
          )}
        </Form>
      </div>
    </>
  )
}
